// Analytics utility for tracking user events and behavior

// Google Analytics tracking
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...parameters,
      app_name: 'Bissa.ai',
      timestamp: new Date().toISOString()
    });
  }
};

// Track page views
export const trackPageView = (pageName, path) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      page_title: pageName,
      page_location: window.location.origin + path,
    });
  }
};

// User engagement tracking
export const trackUserAction = (action, details = {}) => {
  trackEvent('user_engagement', {
    engagement_time_msec: Date.now(),
    action_type: action,
    ...details
  });
};

// CRM-specific event tracking
export const trackCRMEvent = (eventType, entityType, details = {}) => {
  trackEvent('crm_action', {
    event_type: eventType, // 'create', 'edit', 'delete', 'view'
    entity_type: entityType, // 'contact', 'company', 'deal', 'task'
    ...details
  });
};

// Authentication tracking
export const trackAuthEvent = (authAction, method = 'unknown') => {
  trackEvent('auth_event', {
    auth_action: authAction, // 'login', 'logout', 'register', 'password_reset'
    auth_method: method, // 'google', 'email', 'microsoft'
  });
};

// Onboarding tracking
export const trackOnboardingEvent = (step, action = 'view') => {
  trackEvent('onboarding_progress', {
    onboarding_step: step,
    action: action, // 'view', 'complete', 'skip'
  });
};

// Subscription and billing tracking
export const trackBillingEvent = (eventType, planId = null, amount = null) => {
  trackEvent('billing_action', {
    billing_event: eventType, // 'upgrade', 'downgrade', 'cancel', 'payment_success', 'payment_failed'
    plan_id: planId, // 'free', 'growth', 'pro', 'business'
    amount: amount
  });
};

// Feature usage tracking
export const trackFeatureUsage = (featureName, usageType = 'click') => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    usage_type: usageType, // 'click', 'view', 'complete'
  });
};

// Search and discovery tracking
export const trackSearchEvent = (searchTerm, resultCount = 0, searchType = 'general') => {
  trackEvent('search', {
    search_term: searchTerm,
    result_count: resultCount,
    search_type: searchType // 'contacts', 'companies', 'deals', 'help'
  });
};

// Export tracking
export const trackExportEvent = (exportType, recordCount = 0) => {
  trackEvent('data_export', {
    export_type: exportType, // 'csv', 'pdf', 'json'
    record_count: recordCount
  });
};

// Import tracking
export const trackImportEvent = (importType, recordCount = 0, success = true) => {
  trackEvent('data_import', {
    import_type: importType, // 'csv', 'json'
    record_count: recordCount,
    import_success: success
  });
};

// Error tracking
export const trackErrorEvent = (errorType, errorMessage = '', component = 'unknown') => {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit message length
    error_component: component
  });
};

// Performance tracking
export const trackPerformanceEvent = (metricName, value, unit = 'ms') => {
  trackEvent('performance_metric', {
    metric_name: metricName,
    metric_value: value,
    metric_unit: unit
  });
};

// Tutorial and help tracking
export const trackHelpEvent = (helpAction, helpType = 'unknown') => {
  trackEvent('help_engagement', {
    help_action: helpAction, // 'view_tutorial', 'contact_support', 'view_docs'
    help_type: helpType // 'onboarding', 'feature_help', 'troubleshooting'
  });
};

// Social sharing tracking
export const trackShareEvent = (shareMethod, contentType = 'unknown') => {
  trackEvent('share', {
    method: shareMethod, // 'email', 'social', 'link_copy'
    content_type: contentType
  });
};

// Session tracking utilities
export const trackSessionStart = () => {
  trackEvent('session_start', {
    session_id: generateSessionId()
  });
};

export const trackSessionEnd = (duration) => {
  trackEvent('session_end', {
    session_duration: duration
  });
};

// Utility functions
const generateSessionId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Enhanced user identification for analytics
export const identifyUser = (user) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      user_id: user.id,
      custom_map: {
        user_plan: user.plan || 'free',
        user_role: user.role || 'unknown',
        user_company_size: user.companySize || 'unknown'
      }
    });
  }
};

// Consent management for GDPR compliance
export const setAnalyticsConsent = (consentGranted) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: consentGranted ? 'granted' : 'denied'
    });
  }
  
  // Store consent preference
  localStorage.setItem('bissa_analytics_consent', consentGranted ? 'granted' : 'denied');
};

// Check if analytics consent has been granted
export const hasAnalyticsConsent = () => {
  if (typeof window === 'undefined') return false;
  
  const consent = localStorage.getItem('bissa_analytics_consent');
  return consent === 'granted';
};

// Initialize analytics with consent check
export const initializeAnalytics = () => {
  if (typeof window === 'undefined') return;
  
  // Check if user has granted consent
  const hasConsent = hasAnalyticsConsent();
  
  if (hasConsent && window.gtag) {
    // Analytics is ready to use
    trackEvent('analytics_initialized');
  } else {
    // Show consent banner or use without tracking
    console.log('Analytics consent required');
  }
};

export default {
  trackEvent,
  trackPageView,
  trackUserAction,
  trackCRMEvent,
  trackAuthEvent,
  trackOnboardingEvent,
  trackBillingEvent,
  trackFeatureUsage,
  trackSearchEvent,
  trackExportEvent,
  trackImportEvent,
  trackErrorEvent,
  trackPerformanceEvent,
  trackHelpEvent,
  trackShareEvent,
  trackSessionStart,
  trackSessionEnd,
  identifyUser,
  setAnalyticsConsent,
  hasAnalyticsConsent,
  initializeAnalytics
};